import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Paperclip, Download, Trash2, Upload } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Document buckets relevant to a PO. Required vs optional flags help the UI
// show what is "expected" but do NOT block uploads of any allowed type.
const PO_DOC_TYPES = [
  { value: "PO_ANNEXURE", label: "PO Annexure", required: false, hint: "Item-level annexure / scope sheet" },
  { value: "GEM_PO", label: "GeM PO Copy", required: false, hint: "Required for GeM procurements", gemOnly: true },
  { value: "CRAC", label: "CRAC", required: false, hint: "Consignee Receipt & Acceptance Certificate", gemOnly: true },
  { value: "AMENDMENT_LETTER", label: "Amendment Letter", required: false, hint: "Signed amendment notification" },
  { value: "OTHER", label: "Other Supporting Document", required: false },
];

export default function POAttachments({ poId, isGem }) {
  const { token, getAuthHeader } = useAuth();
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [pendingType, setPendingType] = useState(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    if (!poId) return;
    try {
      const r = await axios.get(API_URL + "/po-documents/by-po/" + poId, getAuthHeader());
      setDocs(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [poId]);

  const triggerUpload = (type) => { setPendingType(type); if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); } };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !pendingType) return;
    setBusy(pendingType);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await axios.post(
        API_URL + "/po-documents/upload?purchase_order_id=" + poId + "&document_type=" + pendingType,
        fd,
        { headers: { Authorization: "Bearer " + token, "Content-Type": "multipart/form-data" } }
      );
      toast.success("Uploaded");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(null); setPendingType(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(API_URL + "/documents/" + id, getAuthHeader());
      toast.success("Removed");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleDownload = (id, name) => {
    const url = API_URL + "/documents/" + id + "/download";
    fetch(url, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = name || "document"; a.click();
        window.URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };

  // Filter: GeM-only docs hidden when this isn't a GeM-channel PO
  const visibleTypes = PO_DOC_TYPES.filter(t => !t.gemOnly || isGem);
  const docsByType = docs.reduce((a, d) => { (a[d.document_type] = a[d.document_type] || []).push(d); return a; }, {});

  return (
    <Card data-testid="po-attachments-card">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-5">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-slate-900 text-white"><Paperclip className="w-3.5 h-3.5" /></div>
          <CardTitle className="text-sm font-semibold">PO Attachments</CardTitle>
          <span className="ml-auto text-xs text-slate-500">{docs.length} file{docs.length === 1 ? "" : "s"}</span>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-3">
        <input ref={fileRef} type="file" className="hidden" onChange={handleFile} data-testid="po-doc-file-input" />
        {visibleTypes.map(t => {
          const list = docsByType[t.value] || [];
          return (
            <div key={t.value} className={"p-3 rounded-lg border " + (list.length ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white")} data-testid={"po-doc-row-" + t.value}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{t.label}</span>
                    {list.length > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{list.length} uploaded</Badge>}
                  </div>
                  {t.hint && <p className="text-xs text-slate-500 mt-0.5">{t.hint}</p>}
                </div>
                <Button type="button" size="sm" variant={list.length ? "outline" : "default"} onClick={() => triggerUpload(t.value)} disabled={busy === t.value} data-testid={"po-doc-upload-" + t.value}>
                  <Upload className="w-3 h-3 mr-1" />{busy === t.value ? "Uploading…" : "Upload"}
                </Button>
              </div>
              {list.length > 0 && (
                <div className="mt-2 space-y-1">
                  {list.map(d => (
                    <div key={d.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                      <span className="truncate flex-1 text-slate-600">{d.original_filename} <span className="text-slate-400">· {new Date(d.created_at).toLocaleDateString("en-IN")}</span></span>
                      <Button size="xs" variant="ghost" className="h-7 px-2" onClick={() => handleDownload(d.id, d.original_filename)} data-testid={"po-doc-download-" + d.id}><Download className="w-3 h-3" /></Button>
                      <Button size="xs" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => handleDelete(d.id)} data-testid={"po-doc-delete-" + d.id}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
